/**
 * Communications service — API calls for Email and SMS operations.
 */

import { api } from './api';

export interface EmailPayload {
  recipients: string[];
  company_id?: string;
  subject: string;
  body: string;
}

export interface SmsPayload {
  recipients: string[];
  company_id?: string;
  message: string;
}

export interface EmailSendResponse {
  total_recipients?: number;
  sent: number;
  sent_count?: number;
  failed: number;
  failed_count?: number;
  results?: Array<{ recipient_user_id: string; recipient_address: string; status: string; failure_reason: string | null }>;
  failures?: Array<{ recipient: string; reason: string }>;
  notification?: string;
}

export interface SmsSendResponse {
  sent: Array<{ user_id: string; phone: string; status: string }>;
  sent_count?: number;
  skipped: Array<{ id: string; name: string; last_name: string; email: string; reason: string }>;
  skipped_count?: number;
  warnings: string[];
  failures?: Array<{ recipient: string; reason: string }>;
}

export interface CommunicationLogEntry {
  id: string;
  channel: 'email' | 'sms';
  recipient_user_id: string;
  recipient_address: string;
  recipient_name?: string;
  subject: string | null;
  body: string;
  status: 'sent' | 'delivered' | 'failed' | 'bounced';
  failure_reason: string | null;
  sent_by_user_id: string;
  sent_at: string;
  status_updated_at: string;
}

export interface CommunicationLogResponse {
  data: CommunicationLogEntry[];
  total: number;
  page: number;
  page_size: number;
  pagination?: {
    page: number;
    pageSize?: number;
    limit?: number;
    total: number;
    totalPages: number;
  };
}

export interface LogFilters {
  channel?: 'email' | 'sms';
  status?: 'sent' | 'delivered' | 'failed' | 'bounced';
  page?: number;
  page_size?: number;
}

export const communicationService = {
  async sendEmail(payload: EmailPayload): Promise<EmailSendResponse> {
    const response = await api.post<{ data: EmailSendResponse }>(
      '/admin/communications/email',
      payload,
    );
    return response.data;
  },

  async sendSms(payload: SmsPayload): Promise<SmsSendResponse> {
    const response = await api.post<{ data: SmsSendResponse }>(
      '/admin/communications/sms',
      payload,
    );
    return response.data;
  },

  async getEmailLog(filters?: LogFilters): Promise<CommunicationLogResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.page_size) params.set('pageSize', String(filters.page_size));
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get<{ data: CommunicationLogEntry[]; pagination?: { page: number; pageSize: number; total: number; totalPages: number } }>(`/admin/communications/email/log${query}`);
    return {
      data: response.data ?? [],
      total: response.pagination?.total ?? 0,
      page: response.pagination?.page ?? 1,
      page_size: response.pagination?.pageSize ?? 20,
    };
  },

  async getSmsLog(filters?: LogFilters): Promise<CommunicationLogResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.page_size) params.set('limit', String(filters.page_size));
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get<{ data: CommunicationLogEntry[]; pagination?: { page: number; limit: number; total: number; totalPages: number } }>(`/admin/communications/sms/log${query}`);
    return {
      data: response.data ?? [],
      total: response.pagination?.total ?? 0,
      page: response.pagination?.page ?? 1,
      page_size: response.pagination?.limit ?? 20,
    };
  },
};
